'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState, useEffect } from 'react';
import Badge from '@mui/material/Badge';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import { ShoppingCart, User, Camera } from 'lucide-react';
import { usePathname } from 'next/navigation';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Box } from '@mui/material';
import FaceIcon from '@mui/icons-material/Face';
import CameraEnhanceIcon from '@mui/icons-material/CameraEnhance';
import { getToken, getTokenPayload } from '@/token/token';
import { useSafeSelector } from '@/hooks/useSafeSelector';


const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: -3,
    top: 13,
    border: `2px solid ${(theme.vars ?? theme).palette.background.paper}`,
    padding: '0 4px',
  },
}));

export default function NavbarSection({ config, theme, sticky = true }) {
  const {
    logo,
    logoText,
    backgroundColor,
    textColor,
    logoTextColor,
    links,
    searchPlaceholder,
    searchIcon,
    searchBackgroundColor,
    searchBorderColor,
    searchTextColor,
    cameraIcon,
    cameraLabel,
    loginIcon,
    loginLabel,
    iconColor,
    hoverColor
  } = config;

  const pathname = usePathname();
  const [searchValue, setSearchValue] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const isHomePage = pathname === '/';
  const iconColorToUse = (isHomePage && theme.variant !== 'modern' && theme.variant !== 'elegant') ? 'var(--primary)' : '#D4AF37';
  const {
    UserSessionPayload = {},
  } = useSafeSelector((state) => state.auth, {});

  useEffect(() => {
    // if (typeof window === "undefined") return;

    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  console.log('dssdsd', UserSessionPayload)

  return (
    <nav
      style={{
        backgroundColor: backgroundColor || theme.secondaryColor,
        backgroundImage: theme.variant === 'modern' ? "url('/assets/images/modernHeader.jpg')" : theme.variant === 'elegant' ? "url('/assets/images/elegentHeader.svg')" : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        color: theme.variant === 'elegant' ? '#fff' : (textColor || theme.primaryColor),
        padding: isMobile ? '0.75rem 1rem' : '1rem 3rem',
        position: sticky ? 'sticky' : 'relative',
        top: sticky ? 0 : 'auto',
        zIndex: 1000,
        boxShadow: theme.variant === 'elegant' ? '0 4px 10px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.05)',
        borderBottom: theme.variant === 'elegant' ? 'none' : `1px solid ${config.borderBottom || '#e8d4dc'}`
      }}
    >
      <div
        style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: isMobile ? '1rem' : '2rem'
        }}
      >
        {/* Logo Section */}
        <Link
          href="/"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            textDecoration: 'none',
            minWidth: 'fit-content'
          }}
        >
          <Image
            src={(theme.variant === 'modern' || theme.variant === 'elegant') ? '/assets/Modernlogo.svg' : logo}
            alt="Padami Logo"
            width={isMobile ? 35 : 45}
            height={isMobile ? 35 : 45}
            style={{ objectFit: 'contain' }}
          />
          <span
            style={{
              fontFamily: theme.logoTextFontFamily || 'Elsie Swash Caps, serif',
              fontWeight: theme.logoTextFontWeight || '900',
              fontSize: isMobile ? '24px' : '35px',
              lineHeight: '100%',
              color: (theme.variant === 'modern' || theme.variant === 'elegant') ? '#D4AF37' : (logoTextColor || theme.logoTextColor || '#7B506F')
            }}
          >
            {logoText}
          </span>
        </Link>

        {/* Desktop Navigation Links */}
        {!isMobile && (
          <div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
            {links.map((link, index) => (
              <Link
                key={index}
                href={link.href}
                className="nav-link"
                style={{
                  color: '#000',
                  textDecoration: 'none',
                  fontFamily: theme.navLinkFontFamily || theme.fontFamily,
                  fontWeight: theme.navLinkFontWeight || '700',
                  fontSize: '14px',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap'
                }}
              >
                {link.label}
              </Link>
            ))}
          </div>
        )}

        {/* Search Bar - Hidden on small mobile */}
        <div
          className="search-container"
          style={{
            position: 'relative',
            flex: '1',
            maxWidth: isMobile ? '200px' : '500px'
          }}
        >
          <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', display: 'flex' }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M12.5 11H11.71L11.43 10.73C12.444 9.55407 13.0012 8.05271 13 6.5C13 5.21442 12.6188 3.95772 11.9046 2.8888C11.1903 1.81988 10.1752 0.986756 8.98744 0.494786C7.79973 0.00281635 6.49279 -0.125905 5.23192 0.124899C3.97104 0.375703 2.81285 0.994767 1.90381 1.90381C0.994767 2.81285 0.375703 3.97104 0.124899 5.23192C-0.125905 6.49279 0.00281635 7.79973 0.494786 8.98744C0.986756 10.1752 1.81988 11.1903 2.8888 11.9046C3.95772 12.6188 5.21442 13 6.5 13C8.11 13 9.59 12.41 10.73 11.43L11 11.71V12.5L16 17.49L17.49 16L12.5 11ZM6.5 11C4.01 11 2 8.99 2 6.5C2 4.01 4.01 2 6.5 2C8.99 2 11 4.01 11 6.5C11 8.99 8.99 11 6.5 11Z" fill={theme.variant === 'elegant' ? '#333' : (searchTextColor || "#757575")} />
            </svg>
          </span>
          <input
            type="text"
            placeholder={searchPlaceholder}
            style={{
              width: '100%',
              padding: '0.6rem 1rem 0.6rem 2.8rem',
              borderRadius: theme.variant === 'elegant' ? '30px' : '8px',
              border: theme.variant === 'elegant' ? '1px solid rgba(255,255,255,0.5)' : `1px solid ${searchBorderColor || '#e8d4dc'}`,
              backgroundColor: theme.variant === 'elegant' ? 'rgba(255,255,255,0.95)' : (searchBackgroundColor || '#ffffff'),
              fontSize: '0.95rem',
              color: '#333',
              outline: 'none',
              fontFamily: theme.fontFamily,
              boxShadow: theme.variant === 'elegant' ? 'inset 0 2px 4px rgba(0,0,0,0.1)' : 'none'
            }}
          />
        </div>

        {/* Right Side Icons & Mobile Toggle */}
        <div style={{ display: 'flex', gap: isMobile ? '1.2rem' : '2rem', alignItems: 'center' }}>
          <Link href="/armagic" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textDecoration: 'none', gap: '4px' }}>
            <span style={{ color: iconColorToUse, display: 'flex', alignItems: 'center' }}>
              <Camera size={isMobile ? 20 : 28} strokeWidth={2} />
            </span>
            {!isMobile && <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#000', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{cameraLabel}</span>}
          </Link>

          {!UserSessionPayload?.isTokenExpired ? (
            <Link href="/cart" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textDecoration: 'none', gap: '4px' }}>
              <span style={{ color: (theme.variant === 'modern' || theme.variant === 'elegant') ? '#D4AF37' : 'var(--primary)', display: 'flex', alignItems: 'center' }}>
                <StyledBadge badgeContent={0} color='primary'>
                  <ShoppingCart size={isMobile ? 22 : 28} strokeWidth={2} />
                </StyledBadge>
              </span>
              {!isMobile && <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#000', textTransform: 'uppercase', letterSpacing: '0.5px' }}>CART</span>}
            </Link>
          ) : null}

          {UserSessionPayload?.isTokenExpired ? (
            <Link href="/user/login" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textDecoration: 'none', gap: '4px' }}>
              {theme.variant === 'elegant' ? (
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: iconColorToUse, border: '2px solid #fff', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <User size={18} color="#fff" />
                </div>
              ) : (
                <span style={{ color: iconColorToUse, display: 'flex', alignItems: 'center' }}>
                  <User size={isMobile ? 20 : 28} strokeWidth={2} />
                </span>
              )}
              {!isMobile && <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#000', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{loginLabel}</span>}
            </Link>
          ) : null}

          {!isMobile && !UserSessionPayload?.isTokenExpired ? (
            <Link href="/user/account" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textDecoration: 'none', gap: '4px' }}>
              {theme.variant === 'elegant' ? (
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: iconColorToUse, border: '2px solid #fff', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <User size={18} color="#fff" />
                </div>
              ) : (
                <span style={{ color: iconColorToUse, display: 'flex', alignItems: 'center' }}>
                  <User size={isMobile ? 20 : 28} strokeWidth={2} />
                </span>
              )}
              {!isMobile && <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#000', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{"ACCOUNT"}</span>}
            </Link>
          ) : null}


          {isMobile && (
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={{ background: 'none', border: 'none', padding: '5px', cursor: 'pointer', color: (theme.variant === 'modern' || theme.variant === 'elegant') ? '#fff' : (textColor || theme.primaryColor) }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                {isMobileMenuOpen ? <path d="M18 6L6 18M6 6l12 12" /> : <path d="M4 6h16M4 12h16M4 18h16" />}
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMobile && isMobileMenuOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: backgroundColor || theme.secondaryColor,
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          borderTop: '1px solid rgba(0,0,0,0.05)',
          zIndex: 999
        }}>
          {links.map((link, index) => (
            <Link
              key={index}
              href={link.href}
              onClick={() => setIsMobileMenuOpen(false)}
              style={{
                color: textColor || theme.primaryColor,
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '16px',
                padding: '0.5rem 0'
              }}
            >
              {link.label}
            </Link>
          ))}

          {isMobile && !UserSessionPayload?.isTokenExpired ? (
            <Link
              key={links.length + 1}
              href={'/user/account'}
              onClick={() => setIsMobileMenuOpen(false)}
              style={{
                color: textColor || theme.primaryColor,
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '16px',
                padding: '0.5rem 0'
              }}
            >
              {"ACCOUNT"}
            </Link>
          ) : null}
        </div>
      )}

      <style jsx>{`
        .nav-link {
          transition: color 0.3s ease;
        }
        .nav-link:hover {
          color: ${hoverColor || '#8b3a6a'} !important;
        }
        @media (max-width: 600px) {
          .search-container {
            display: none;
          }
        }
      `}</style>
    </nav>
  );
}
